import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ErroApi } from '../lib/api.ts';
import { formatarTelefone } from '../lib/formato.ts';
import { linkWhatsapp, prepararTelefone } from '../lib/whatsapp.ts';
import { Aviso } from './forma.tsx';
import { Lista } from './painel.tsx';
import './avisarWhatsapp.css';

interface MedicoAviso {
  id: string;
  nome: string;
  telefone: string | null;
  aceitaWhatsapp: boolean;
  quantidade: number;
  diasEsperando: number;
  mensagem: string;
}

/** Um aviso por médico, e não um por exame.
 *
 *  Num dia cheio, notificar exame a exame são dezenas de mensagens — e o médico
 *  silencia o número na primeira semana. Aqui cada médico recebe um resumo do
 *  que está esperando por ele, sem nome de paciente: aviso interno não precisa
 *  carregar dado clínico por um aplicativo pessoal. */
export function AvisarMedicos() {
  const dados = useQuery({
    queryKey: ['aviso', 'medicos'],
    queryFn: ({ signal }) =>
      api.get<{ itens: MedicoAviso[]; semSite: boolean }>('/aviso/medicos', signal),
    staleTime: 30_000,
  });

  const marcar = useMutation({
    mutationFn: (medicoId: string) => api.post(`/aviso/medico/${medicoId}`),
  });

  const itens = dados.data?.itens ?? [];

  return (
    <>
      {dados.data?.semSite && (
        <Aviso tipo="info">
          Falta cadastrar o endereço do site em Configurações — sem ele o link da mensagem sai
          quebrado.
        </Aviso>
      )}

      <Lista
        carregando={dados.isPending}
        erro={dados.error instanceof ErroApi ? dados.error.message : undefined}
        vazio={
          itens.length === 0
            ? {
                titulo: 'Nenhum médico com exame esperando',
                apoio: 'Aparecem aqui os médicos com exame já radiografado e ainda sem laudo.',
              }
            : undefined
        }
      >
        <div className="avisar-medicos">
          {itens.map((m) => {
            const tel = prepararTelefone(m.telefone);
            const impedido = !m.aceitaWhatsapp || !!tel.problema;
            return (
              <div className="avisar-medico" key={m.id} data-atrasado={m.diasEsperando >= 3 || undefined}>
                <span className="avisar-medico__quantidade">{m.quantidade}</span>
                <span className="avisar-medico__nome">
                  {m.nome}
                  <span className="avisar-medico__apoio">
                    {m.diasEsperando <= 0
                      ? 'o mais antigo é de hoje'
                      : `o mais antigo espera há ${m.diasEsperando} dia(s)`}
                    {tel.problema
                      ? ` · ${tel.problema}`
                      : !m.aceitaWhatsapp
                        ? ' · não autorizou mensagens'
                        : ` · ${formatarTelefone(m.telefone)}`}
                  </span>
                </span>
                <a
                  className={`botao botao--primario botao--sm${impedido ? ' botao--travado' : ''}`}
                  href={impedido ? undefined : linkWhatsapp(tel.internacional!, m.mensagem)}
                  target="_blank"
                  rel="noreferrer"
                  aria-disabled={impedido ? true : undefined}
                  onClick={(e) => {
                    if (impedido) {
                      e.preventDefault();
                      return;
                    }
                    marcar.mutate(m.id);
                  }}
                >
                  Avisar
                </a>
              </div>
            );
          })}
        </div>
      </Lista>
    </>
  );
}
